import {Link, Outlet, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Stack, Tab, Tabs, Typography} from "@mui/material";
import DashboardIcon from "@mui/icons-material/DashboardRounded";
import UserIcon from "@mui/icons-material/PersonRounded";
import CompanyIcon from "@mui/icons-material/GroupRounded";
import OrdersIcon from "@mui/icons-material/ShoppingCartRounded";
import IrritilesIcon from "@mui/icons-material/ExtensionRounded";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FarmsIcon from "@mui/icons-material/Agriculture";
import {AppLayout} from "../AppLayout";
import {AuthorizationGuard} from "../AuthorizationGuard";
import {UserGlobalRole} from "../../types/domain/user";
import {MenuItem} from "../../types/menuItem";
import {Campaign, Flag} from "@mui/icons-material";

export const AdminPage: React.FC = () => (
    <AuthorizationGuard permitAnyOfGlobalRoles={[UserGlobalRole.ADMIN, UserGlobalRole.CS, UserGlobalRole.HYDROSAT]}>
        <AppLayout sidePanel={<AdminPagePanel />}>
            <Outlet />
        </AppLayout>
    </AuthorizationGuard>
);

const AdminPagePanel: React.FC = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const pathPrefix = location.pathname?.split("/", 3).join("/");
    const selectedTabValue = ADMIN_MENU_VIABLE_PATHS.includes(pathPrefix) ? pathPrefix : false;

    return (
        <Stack
            direction="column"
            sx={{height: "calc(100% - 2.25rem)", padding: "1.125rem"}}
            overflow="hidden"
            gap="1.5rem"
        >
            <Typography color="primary" fontSize={22}>
                {t("admin_page.title")}
            </Typography>
            <Tabs
                value={selectedTabValue}
                orientation="vertical"
                textColor="inherit"
                variant="fullWidth"
                TabIndicatorProps={{
                    sx: {
                        width: "3px",
                        left: 0,
                    },
                }}
                sx={{
                    bgcolor: "#F9FAFC",
                    minHeight: "auto",
                    color: "#000000A0",
                    "& a:hover": {backgroundColor: "#eceff5"},
                    "& .Mui-selected": {color: "#4855bf"},
                }}
            >
                {menuItems.map((item) => (
                    <Tab
                        color="primary"
                        key={item.href}
                        label={t(item.i18nLabel)}
                        icon={item.icon}
                        iconPosition="start"
                        value={item.href}
                        id={item.href}
                        component={Link}
                        to={item.href}
                        sx={{
                            minHeight: "3rem",
                            height: "3rem",
                            letterSpacing: ".04em",
                            justifyContent: "left",
                            paddingX: "1.25rem",
                            opacity: 1,
                            fontSize: 14,
                            lineHeight: "1.25rem",
                            whiteSpace: "nowrap",
                        }}
                    />
                ))}
            </Tabs>
        </Stack>
    );
};

const menuItems: MenuItem[] = [
    {
        i18nLabel: "admin_page.menu.dashboard",
        href: "/admin",
        icon: <DashboardIcon />,
    },
    {
        i18nLabel: "admin_page.menu.users",
        href: "/admin/users",
        icon: <UserIcon />,
    },
    {
        i18nLabel: "admin_page.menu.companies",
        href: "/admin/companies",
        icon: <CompanyIcon />,
    },
    {
        i18nLabel: "admin_page.menu.orders",
        href: "/admin/orders",
        icon: <OrdersIcon />,
    },
    {
        i18nLabel: "admin_page.menu.farms",
        href: "/admin/farms",
        icon: <FarmsIcon />,
    },
    {
        i18nLabel: "admin_page.menu.irritiles",
        href: "/admin/irritiles",
        icon: <IrritilesIcon />,
    },
    {
        i18nLabel: "admin_page.menu.fieldChanges",
        href: "/admin/field-changes",
        icon: <TaskAltIcon />,
    },
    {
        i18nLabel: "admin_page.menu.featureFlags",
        href: "/admin/feature-flags",
        icon: <Flag />,
    },
    {
        i18nLabel: "admin_page.menu.announcements",
        href: "/admin/announcements",
        icon: <Campaign />,
    },
];

const ADMIN_MENU_VIABLE_PATHS = menuItems.map((item) => item.href);
